/** Site-level content. Facts come from the previous version of this site or the
 *  project READMEs (see src/components/work/Data.jsx). Items marked VERIFY need
 *  the owner's confirmation before launch (docs/01-audit-and-strategy.md §7). */

export const profile = {
  name: "Samartha H V",
  handle: "DEADSERPENT",
  role: "Software engineer",
  focus: "ML systems, privacy-preserving learning, developer tools and GPU software",
  location: "Bengaluru, India",
  email: "samarthsmg14@gmail.com",
  github: "https://github.com/DEADSERPENT",
  linkedin: "https://www.linkedin.com/in/samarthasmg",
  telegram: "https://t.me/SAMARTHASMG",
  behance: "https://www.behance.net/samarthhv",
  figma: "https://www.figma.com/@samarthahv",
  scholar: "https://scholar.google.com/citations?user=brgSQnwAAAAJ&hl=en",
  orcid: "https://orcid.org/0009-0007-9780-5794",
};

export const nav = [
  { id: "work", label: "Work" },
  { id: "research", label: "Research" },
  { id: "notes", label: "Notes" },
  { id: "skills", label: "Skills" },
  { id: "about", label: "About" },
  { id: "contact", label: "Contact" },
];

/** What I build: counts are derived from the project list at render time. */
export const buildAreas = [
  { domain: "privacy", title: "Private learning", line: "Models that learn without seeing anyone's raw data." },
  { domain: "ml", title: "Trustworthy ML", line: "Models that watch and check themselves." },
  { domain: "vision", title: "Computer vision", line: "Judging quality from images." },
  { domain: "hpc", title: "GPU computing", line: "Fast code that stays correct." },
  { domain: "tools", title: "Developer tools", line: "Tools other engineers install and use." },
  { domain: "backend", title: "Backend systems", line: "Services that stay safe when things fail." },
];

/** Technology stack. Source: the current Overleaf resume, the earlier CV and the original portfolio.
 *  `use`   = on the resume, or rated Intermediate or above on the original portfolio.
 *  `learn` is kept for future use and is currently empty. */
export const stack = [
  {
    layer: "Languages",
    use: ["Python", "JavaScript", "SQL", "HTML", "CSS"],
    learn: [],
  },
  {
    layer: "AI and machine learning",
    use: ["NLP", "Transformers", "RAG", "Federated learning", "Scikit-learn", "LightGBM", "MLflow", "LangChain", "FAISS", "Ollama", "MCP", "Pandas", "NumPy"],
    learn: [],
  },
  {
    layer: "GPU computing",
    use: ["CUDA", "C++"],
    learn: [],
  },
  {
    layer: "Backend and data",
    use: ["Node.js", "Express.js", "FastAPI", "REST APIs", "PostgreSQL", "MySQL", "Firebase", "Data pipelines", "Data modeling"],
    learn: [],
  },
  {
    layer: "Frontend",
    use: ["React", "Angular", "Bootstrap", "Tailwind CSS"],
    learn: [],
  },
  {
    layer: "DevOps and tools",
    use: ["Docker", "Git", "GitHub", "GitLab", "Jenkins", "Linux", "Windows", "Jupyter", "Postman"],
    learn: [],
  },
  {
    layer: "Design and media",
    use: ["Figma", "Photoshop", "Illustrator", "Lightroom", "Canva", "Blender", "DaVinci Resolve"],
    learn: [],
  },
];

/** Lessons taken from bugs documented in the READMEs. */
export const notes = [
  {
    project: "cudanet",
    title: "One optimizer fix recovered 68 points",
    body: "The batch-norm path used the SGD update whatever optimizer was chosen. Dispatching correctly lifted BN + Adam + LR from 17.91% to 85.64%.",
    lesson: "Check the plumbing before the model.",
    visual: {
      type: "beforeAfter",
      before: { label: "Before the fix", value: 17.91 },
      after: { label: "After the fix", value: 85.64 },
      unit: "% test accuracy",
    },
  },
  {
    project: "drifting-oracle",
    title: "Closing a data leak",
    body: "About 10.5% of the held-out set overlapped the Champion's training fold. An ID-hashed holdout reversed the result: the Challenger wins, 0.6942 vs 0.6873.",
    lesson: "Own the evaluation split in one place.",
    visual: { type: "overlap", percent: 10.5, a: "Champion training fold", b: "“Held-out” comparison set" },
  },
  {
    project: "pp-hfm",
    title: "Privacy per patient",
    body: "Each patient contributes about eight windows, so the privacy budget is counted per patient, matching the clinical question.",
    lesson: "Choose the privacy unit first.",
    visual: { type: "units", groups: 4, per: 8 },
  },
  {
    project: "federated-traffic",
    title: "Reading a small gap",
    body: "Over five runs the federated and local-only ranges overlap, so the case for federating rests on privacy and robustness.",
    lesson: "Report the spread.",
    visual: {
      type: "range",
      unit: "s",
      axis: [8.7, 9.5],
      items: [
        { label: "Local-only learning", mean: 9.13, sd: 0.22 },
        { label: "Federated learning", mean: 9.06, sd: 0.18, highlight: true },
      ],
    },
  },
];

/** Profile links shown in the footer. An empty `url` hides the entry, so no
 *  profile is linked until its real address has been confirmed. */
export const socials = [
  { id: "github", label: "GitHub", mark: "Gh", handle: "DEADSERPENT", url: "https://github.com/DEADSERPENT" },
  { id: "googlescholar", label: "Google Scholar", mark: "Gs", handle: "Publications", url: "https://scholar.google.com/citations?user=brgSQnwAAAAJ&hl=en" },
  { id: "orcid", label: "ORCID", mark: "Oc", handle: "0009-0007-9780-5794", url: "https://orcid.org/0009-0007-9780-5794" },
  { id: "linkedin", label: "LinkedIn", mark: "In", handle: "samarthasmg", url: "https://www.linkedin.com/in/samarthasmg" },
  { id: "telegram", label: "Telegram", mark: "Tg", handle: "@SAMARTHASMG", url: "https://t.me/SAMARTHASMG" },
  { id: "instagram", label: "Instagram", mark: "Ig", handle: "@samarthsmg", url: "https://www.instagram.com/samarthsmg/" },
  { id: "behance", label: "Behance", mark: "Be", handle: "samarthhv", url: "https://www.behance.net/samarthhv" },
  { id: "figma", label: "Figma", mark: "Fg", handle: "@samarthahv", url: "https://www.figma.com/@samarthahv" },
  { id: "x", label: "X", mark: "X", handle: "@SAMARTH_Hv", url: "https://x.com/SAMARTH_Hv" },
  { id: "facebook", label: "Facebook", mark: "Fb", handle: "SAMARTHASMG", url: "https://www.facebook.com/SAMARTHASMG" },
  { id: "deviantart", label: "DeviantArt", mark: "Da", handle: "samarthsmg", url: "https://www.deviantart.com/samarthsmg" },
  { id: "pinterest", label: "Pinterest", mark: "Pi", handle: "deadserpent", url: "https://in.pinterest.com/deadserpent/" },
];

/* `from` / `to` / `at` place an entry on the career chart as decimal years (2025.5 is mid-2025).
   Year-only entries are drawn at mid-year; the printed dates are the source of truth. `to: "now"` runs to today. */
export const education = [
  { title: "M.Tech, Computer Science", place: "Manipal Institute of Technology", when: "2025 – present", from: 2025.58, to: "now" },
  { title: "B.E., Computer Science", place: "MVJ College of Engineering", when: "2021 – 2025", from: 2021.5, to: 2025.5 },
  { title: "PCMB", place: "Sri Chaitanya PU College", when: "2019 – 2021", from: 2019.5, to: 2021.5 },
];

export const experience = [
  { title: "Advisor", place: "MITB ACM Student Chapter", when: "Oct 2025 – Oct 2026", from: 2025.75, to: 2026.8 },
  { title: "Software Developer Intern", place: "SHLR Technosoft Pvt. Ltd.", when: "Feb 2025 – May 2025", from: 2025.08, to: 2025.42 },
  { title: "AWS Intern", place: "SkillForge", when: "Oct 2024 – Dec 2024", from: 2024.75, to: 2025.0 },
  { title: "Web Developer Intern", place: "Intrainz Innovation", when: "Dec 2023 – Feb 2024", from: 2023.92, to: 2024.17 },
  { title: "Graphic Designer", place: "GDSC MVJCE", when: "2022 – 2025", from: 2022.5, to: 2025.5 },
  { title: "Graphic Designer", place: "TEDx MVJCE", when: "2021 – 2024", from: 2021.5, to: 2024.5 },
];

/** Peer-reviewed work. Everything here is taken from the Google Scholar profile. */
export const publications = [
  {
    id: "cudanet-ieee",
    title: "CUDAnet: A Micro-Footprint, Dependency-Free Deep Learning Engine for Edge-Native GPU Training",
    authors: ["HV Samartha", "R Chiranth", "N Pavithra"],
    me: "HV Samartha",
    venue: "2026 IEEE International Conference on Natural Language Processing and Computer Vision (ICNPCV)",
    presented: "Presented at Global Academy of Technology, Bengaluru, July 2026",
    publisher: "IEEE",
    date: "17 July 2026",
    summary:
      "A from-scratch CUDA implementation of a multi-layer perceptron that fixes a correctness problem in GPU-parallel stateful optimizers. Parallelising Momentum or Adam with one thread per sample makes threads collide on shared optimizer state; a two-phase gradient accumulation removes the race.",
    url: "https://ieeexplore.ieee.org/abstract/document/11708751/",
    project: "cudanet",
  },
];

/** Research directions listed on the Google Scholar profile. */
export const researchInterests = [
  "Artificial Intelligence",
  "Deep Learning",
  "GPU Computing",
  "Federated Learning",
  "Computer Vision",
];

/** Case files that are research in progress (shown beside the paper). */
export const researchInProgress = ["pp-hfm", "federated-traffic"];

/** Best credentials, from the Credly, LinkedIn and Coursera profiles. Each links to its own verification page. */
export const certifications = [
  {
    title: "Google Cloud Computing Foundations Certificate",
    place: "Google Cloud",
    when: "Apr 2025",
    at: 2025.25,
    url: "https://www.credly.com/badges/349db0c4-e02d-473f-b5ed-a93cbb933ea6",
  },
  {
    title: "Cloud Computing Fundamentals (Intermediate)",
    place: "IBM SkillsBuild",
    when: "Apr 2026",
    at: 2026.26,
    url: "https://www.credly.com/badges/88165cd4-d077-4363-a755-725fbc2a2b66",
  },
  {
    title: "Google AI Essentials",
    place: "Coursera",
    when: "Aug 2026",
    at: 2026.62,
    url: "https://www.credly.com/badges/46fb9e8e-1f5f-49ce-98b9-871509c9e1f0",
  },
  {
    title: "Google UX Design Professional Certificate",
    place: "Coursera",
    when: "Dec 2024",
    at: 2024.96,
    url: "https://coursera.org/verify/professional-cert/Q04J1V6XB2E6",
  },
];

/** Every badge, on Credly (two accounts). */
export const credlyProfiles = [
  { label: "Google and Coursera badges", url: "https://www.credly.com/users/samartha-h/badges" },
  { label: "IBM and AWS badges", url: "https://www.credly.com/users/samartha-h-v/badges" },
];
